import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'insidePanama_bookmarks';
const EVENT = 'bookmarks-change';

/**
 * Reads + manages the user's bookmarked articles. Bookmarks live in
 * localStorage as `[{ slug, lang, category, title, savedAt }]`.
 *
 * Cross-component sync via a custom `bookmarks-change` window event so any
 * mounted hook stays in sync.
 *
 * Returns:
 *   { items, isBookmarked(slug, lang), toggle(article), clear() }
 *
 * @example
 *   const { items, isBookmarked, toggle } = useBookmarks();
 *   <button onClick={() => toggle({ slug, lang, category, title })}>
 */
export function useBookmarks() {
  const [items, setItems] = useState(() => read());

  useEffect(() => {
    const onChange = () => setItems(read());
    window.addEventListener(EVENT, onChange);
    window.addEventListener('storage', onChange); // cross-tab sync
    return () => {
      window.removeEventListener(EVENT, onChange);
      window.removeEventListener('storage', onChange);
    };
  }, []);

  const isBookmarked = useCallback(
    (slug, lang) => items.some((b) => b.slug === slug && b.lang === lang),
    [items],
  );

  const toggle = useCallback(({ slug, lang, category, title }) => {
    if (!slug) return;
    const current = read();
    const exists = current.find((b) => b.slug === slug && b.lang === lang);
    const next = exists
      ? current.filter((b) => !(b.slug === slug && b.lang === lang))
      : [...current, { slug, lang, category, title, savedAt: Date.now() }];
    write(next);
    setItems(next);
    window.dispatchEvent(new CustomEvent(EVENT));
  }, []);

  const clear = useCallback(() => {
    write([]);
    setItems([]);
    window.dispatchEvent(new CustomEvent(EVENT));
  }, []);

  return { items, isBookmarked, toggle, clear };
}

function read() {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function write(value) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    /* private mode / quota — ignore */
  }
}
